import React from 'react';
import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';

const formatTime = (seconds = 0) => new Date(seconds * 1000).toISOString().substr(11, 8);

interface IWithWidth {
  width: number;
}

export const StyledProgressWrapper = styled.div<IWithWidth>`
  width: ${({ width }) => scaleSize(width)}px;
  height: ${scaleSize(48)}px;
  position: relative;
  margin-top: ${scaleSize(40)}px;
  left: ${scaleSize(220)}px;
`;

export const StyledProgressFull = styled.div<IWithWidth>`
  width: ${({ width }) => scaleSize(width)}px;
  height: ${scaleSize(8)}px;
  background: ${({ theme }) => theme.playerProgressBackground};
  position: absolute;
  top: ${scaleSize(20)}px;
  border-radius: 2px;
  overflow: hidden;
`;

interface IWithProgress {
  progress: number;
}

export const StyledProgressElapsed = styled.div.attrs<IWithProgress>(({ progress }) => ({
  style: { transform: `scale(${scaleSize(progress)})` },
}))<IWithProgress>`
  width: 1px;
  height: ${scaleSize(8)}px;
  background: ${({ theme }) => theme.playerProgressElapsed};
  position: absolute;
  transform-origin: left;
  top: 0;
  left: 0;
  z-index: 2;
`;

interface IStyledCurrentPosition {
  progress: number;
  isSeeking: boolean;
}

export const StyledCurrentPosition = styled.div.attrs<IStyledCurrentPosition>(({ progress }) => ({
  style: { transform: `translate3d(${scaleSize(progress)}px, 0, 0)` },
}))<IStyledCurrentPosition>`
  width: ${scaleSize(40)}px;
  height: ${scaleSize(40)}px;
  position: absolute;
  top: ${scaleSize(4)}px;
  left: -${scaleSize(20)}px;
  z-index: 3;
  cursor: pointer;

  &:before {
    width: ${scaleSize(40)}px;
    height: ${scaleSize(40)}px;
    display: block;
    content: '';
    background-color: ${({ theme }) => theme.playerCurrentPosition};
    border-radius: 50%;
  }

  &:hover {
    &:before {
      transform: scale(1.2);
      background: ${({ theme }) => theme.activeColor};
    }
  }

  & > span {
    width: ${scaleSize(130)}px;
    height: ${scaleSize(48)}px;
    font-size: ${scaleSize(30)}px;
    line-height: ${scaleSize(48)}px;
    position: absolute;
    left: -${scaleSize(45)}px;
    bottom: -${scaleSize(48)}px;
    display: block;
    text-align: center;
    color: ${({ theme }) => theme.playerCurrentPosition};
    opacity: ${({ isSeeking }) => (isSeeking ? 1 : 0)};
  }
`;

export const StyledTimeLabel = styled.div<{ isSeeking: boolean }>`
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(48)}px;
  position: relative;
  font-family: 'RobotoMedium', sans-serif;
  font-size: ${scaleSize(30)}px;
  line-height: ${scaleSize(48)}px;
  color: ${({ theme }) => theme.playerTime};
  text-align: center;
  opacity: ${({ isSeeking }) => (isSeeking ? 0 : 1)};

  & > span {
    opacity: 0.7;
  }
`;

interface IProgressBarProps {
  width: number;
  seekPending: number;
  currentTime: number;
  duration: number;
}

const calculateProgress = (width, { currentTime, duration, seekPending }) => {
  const value = duration ? (width / duration) * (seekPending || currentTime) : 0;
  return Number(value.toFixed(2));
};

const ProgressBar: React.FC<IProgressBarProps> = (props) => {
  const { width, seekPending, currentTime, duration } = props;

  const progress = calculateProgress(width, { currentTime, duration, seekPending });
  return (
    <>
      <StyledProgressWrapper width={width}>
        <StyledCurrentPosition progress={progress} isSeeking={seekPending !== null}>
          <span>{formatTime(seekPending || 0)}</span>
        </StyledCurrentPosition>
        <StyledProgressFull width={width}>
          <StyledProgressElapsed progress={progress} />
        </StyledProgressFull>
      </StyledProgressWrapper>
      <StyledTimeLabel isSeeking={seekPending !== null}>
        {formatTime(currentTime)} / <span>{formatTime(duration)}</span>
      </StyledTimeLabel>
    </>
  );
};

export default ProgressBar;
